import { FORUM_PUBLISH_BATCH_KEY } from './constants';
import { storableError } from '../errors';
import * as log from '../log';

export const clearForumPublishBatchId = (sdk, listingId) =>
  sdk.ownListings.update({
    id: listingId,
    privateData: { [FORUM_PUBLISH_BATCH_KEY]: null },
  });

const readPublishedAt = listingResponse =>
  listingResponse?.data?.data?.attributes?.publicData?.publishedAt;

/**
 * Live publishedAt, or null when missing / unreadable.
 * A failed show must not abort publish — treat as missing so the caller can still stamp.
 */
const fetchExistingPublishedAt = async (sdk, listingId) => {
  try {
    const res = await sdk.ownListings.show({ id: listingId });
    return readPublishedAt(res) ?? null;
  } catch (e) {
    return null;
  }
};

/**
 * Strip a leftover Finish-table batch id so one-off publishes always land on dest forums.
 * Clear failures do not block publish.
 */
export const publishDraftWithoutForumBatch = async (sdk, listingId, queryParams) => {
  try {
    await clearForumPublishBatchId(sdk, listingId);
  } catch (e) {
    // Listing may already lack privateData; still publish.
  }
  try {
    await stampPublishedAtIfMissing(sdk, listingId);
  } catch (e) {
    log.error(e, 'listing-published-at-stamp-failed', { listingId });
  }
  return sdk.ownListings.publishDraft({ id: listingId }, queryParams);
};

/**
 * Stamp publicData.publishedAt with the current unix-ms time if it is missing.
 * Should be called once before the first publishDraft so search "Newest" is stable.
 *
 * Callers decide whether to swallow failures (one-off path swallows; batch path skips row).
 */
export const stampPublishedAtIfMissing = async (sdk, listingId) => {
  const existing = await fetchExistingPublishedAt(sdk, listingId);
  if (existing != null) {
    return;
  }

  await sdk.ownListings.update({
    id: listingId,
    publicData: { publishedAt: Date.now() },
  });
};

const listingIdStr = listingOrId => {
  if (typeof listingOrId === 'string') {
    return listingOrId;
  }
  if (listingOrId?.uuid) {
    return listingOrId.uuid;
  }
  if (listingOrId?.id?.uuid) {
    return listingOrId.id.uuid;
  }
  if (typeof listingOrId?.id === 'string') {
    return listingOrId.id;
  }
  return listingOrId;
};

/**
 * Stamp one batch id on every selected draft, and stamp publicData.publishedAt if missing.
 * Then publishDraft sequentially.
 *
 * If stamp/update fails for a row, skip publishing that row.
 * If publishDraft fails, best-effort clear the batch id so later publishes aren't mis-tagged.
 */
export const publishSelectedWithForumBatch = async ({
  sdk,
  listings = [],
  createBatchId = () => crypto.randomUUID(),
} = {}) => {
  const batchId = createBatchId();
  const errors = [];
  const publishedIds = [];

  for (const listing of listings) {
    const listingId = listing?.id;
    const idStr = listingIdStr(listingId);

    try {
      const existingPublishedAt = await fetchExistingPublishedAt(sdk, listingId);
      await sdk.ownListings.update({
        id: listingId,
        privateData: { [FORUM_PUBLISH_BATCH_KEY]: batchId },
        ...(existingPublishedAt == null ? { publicData: { publishedAt: Date.now() } } : {}),
      });
    } catch (e) {
      errors.push({ listingId: idStr, ...storableError(e) });
      continue;
    }

    try {
      await sdk.ownListings.publishDraft({ id: listingId }, { expand: true });
      publishedIds.push(idStr);
    } catch (e) {
      try {
        await clearForumPublishBatchId(sdk, listingId);
      } catch (_) {
        // Best effort so a later Edit Listing publish is not treated as this batch.
      }
      errors.push({ listingId: idStr, ...storableError(e) });
    }
  }

  return { batchId, errors, publishedIds };
};
